import * as Yup from 'yup';
import { ErrorMessage, Field, Form, Formik } from 'formik';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import LoadingButton from '@mui/lab/LoadingButton';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import HttpService from '../../../hooks/Https-services';
import { API_ROUTES, ROLE_IDS } from '../../../utils/constant';
import { toast } from 'react-toastify';
import React, { useState, useEffect, useCallback } from 'react';
import {
  Backdrop,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from '@mui/material';
import dayjs from 'dayjs';

type Props = {
  open: boolean;
  onClose: VoidFunction;
  fetchUsersList: any;
};

export default function BulkShiftUpload({
  open,
  onClose,
  fetchUsersList,
}: Props) {
  const [teams, setTeams] = useState([]);
  const [shifts, setShifts] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  const validationSchema = Yup.object().shape({
    shiftId: Yup.string().required('Shift is required'),
    roleId: Yup.number().required('Role is required'),
    teamId: Yup.number().required('Team is required'),
  });

  // Fetch teams list
  const fetchTeamsList = useCallback(async () => {
    try {
      const response = await HttpService.get(API_ROUTES.fatchAllTeam);
      setTeams(response.data.data);
    } catch (error) {
      console.error('Error fetching teams list:', error);
    }
  }, []);

  // Fetch shifts list based on selected role and team
  const fetchShiftsList = useCallback(async (roleId: any, teamId: any) => {
    const query: any = { roleId, teamId };
    try {
      setLoading(true);
      const response = await HttpService.get(
        API_ROUTES.fatchShift + `?${new URLSearchParams(query).toString()}`
      );
      setShifts(response.data.data.list || []);
    } catch (error) {
      console.error('Error fetching shifts list:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Trigger shifts fetch when roleId or teamId change
  useEffect(() => {
    if (open) {
      fetchTeamsList(); // Fetch teams when the modal is opened
    }
  }, [open, fetchTeamsList]);

  return (
    <Dialog fullWidth open={open} onClose={onClose}>
      <Formik
        initialValues={{
          shiftId: '',
          roleId: 1,
          teamId: 1,
        }}
        enableReinitialize
        validationSchema={validationSchema}
        onSubmit={async (values) => {
          const { shiftId, roleId, teamId } = values;
          const info = { shiftId, roleId, teamId };

          try {
            await HttpService.post(API_ROUTES.bulkShiftUpdate, info);
            toast.success('Bulk Shift Update Successfully', {
              position: 'bottom-right',
            });
            fetchUsersList();
            onClose();
          } catch (error) {
            console.error(error);
            onClose();
          }
        }}
      >
        {({ values, errors, touched, isSubmitting, setFieldValue }) => {
          // Watch for changes in roleId and teamId to fetch shifts
          useEffect(() => {
            if (values.roleId && values.teamId) {
              fetchShiftsList(values.roleId, values.teamId);
            }
          }, [values.roleId, values.teamId, fetchShiftsList]);

          return (
            <Form>
              <DialogTitle>Bulk Update Shift</DialogTitle>
              <DialogContent>
                <Box
                  rowGap={3}
                  columnGap={2}
                  display="grid"
                  gridTemplateColumns={{
                    xs: 'repeat(1, 1fr)',
                    sm: 'repeat(2, 1fr)',
                  }}
                  sx={{ marginTop: '10px' }}
                >
                  {/* Role Dropdown */}
                  <FormControl fullWidth margin="normal">
                    <InputLabel id="role-select-label">Role</InputLabel>
                    <Field
                      name="roleId"
                      as={Select}
                      labelId="role-select-label"
                      label="Role"
                      helperText={
                        touched.roleId && errors.roleId ? errors.roleId : null
                      }
                      error={touched.roleId && !!errors.roleId}
                    >
                      {Object.entries(ROLE_IDS).map(([key, id]) => (
                        <MenuItem key={id} value={id}>
                          {key.toLowerCase()}{' '}
                          {/* You can display the role name here */}
                        </MenuItem>
                      ))}
                    </Field>
                    <ErrorMessage name="roleId" component="div" />
                  </FormControl>

                  {/* Team Dropdown */}
                  <FormControl fullWidth margin="normal">
                    <InputLabel id="team-select-label">Teams</InputLabel>
                    <Field
                      name="teamId"
                      as={Select}
                      labelId="team-select-label"
                      label="Teams"
                      helperText={
                        touched.teamId && errors.teamId ? errors.teamId : null
                      }
                      error={touched.teamId && !!errors.teamId}
                    >
                      {teams.map((team: any) => (
                        <MenuItem key={team.id} value={team.id}>
                          {team.userTeamKey}
                        </MenuItem>
                      ))}
                    </Field>
                    <ErrorMessage name="teamId" component="div" />
                  </FormControl>

                  {/* Shift Dropdown */}
                  <FormControl fullWidth margin="normal">
                    <InputLabel id="shift-select-label">Shift</InputLabel>
                    <Field
                      name="shiftId"
                      as={Select}
                      labelId="shift-select-label"
                      label="Shift"
                      helperText={
                        touched.shiftId && errors.shiftId
                          ? errors.shiftId
                          : null
                      }
                      error={touched.shiftId && !!errors.shiftId}
                    >
                      {shifts.length > 0 ? (
                        shifts.map((shift: any) => (
                          <MenuItem key={shift.id} value={shift.id}>
                            {shift.shiftName} &nbsp; &nbsp; Start Time:{' '}
                            {dayjs(shift?.startTime).format('HH:mm') || '00:00'}{' '}
                            &nbsp; &nbsp; End Time:{' '}
                            {dayjs(shift?.endTime).format('HH:mm') ||
                              '00:00:00'}
                          </MenuItem>
                        ))
                      ) : (
                        <MenuItem disabled>
                          No Shift Found For This Role or Team
                        </MenuItem>
                      )}
                    </Field>
                    <ErrorMessage name="shiftId" component="div" />
                  </FormControl>
                </Box>
              </DialogContent>

              <DialogActions>
                <Button
                  variant="outlined"
                  onClick={onClose}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  Cancel
                </Button>
                <LoadingButton
                  type="submit"
                  variant="contained"
                  loading={isSubmitting}
                  disabled={
                    isSubmitting ||
                    !values.shiftId ||
                    !values.roleId ||
                    !values.teamId
                  }
                >
                  Update Shift
                </LoadingButton>
              </DialogActions>
            </Form>
          );
        }}
      </Formik>

      {/* Backdrop loader */}
      <Backdrop
        sx={(theme) => ({ color: '#fff', zIndex: theme.zIndex.drawer + 1 })}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </Dialog>
  );
}
